<template>
  <AuthLayout
      class="register-external-page"
      active-tab="register"
      :title="currentStep === 2 ? 'Már csak egy lépésre a<br>kényelmes könyveléstől!' : 'Köszöntünk a kényelmes<br>könyvelés világában!'"
      dimmed
  >
    <template #steps>
      <div class="auth-step-list">
        <div
            v-for="item in stepStates"
            :key="item.id"
            class="auth-step-item"
            :class="{ active: currentStep === item.id, complete: currentStep > item.id }"
            role="button"
            tabindex="0"
            @click="onStepClick(item.id)"
            @keyup.enter="onStepClick(item.id)"
            @keyup.space="onStepClick(item.id)"
        >
          <span class="auth-step-counter">
            <i v-if="currentStep > item.id" class="fas fa-check"></i>
            <span v-else>{{ item.id }}</span>
          </span>
          <span class="auth-step-copy">
            <span class="font-jumperlight">{{ $cte(item.title) }}</span>
            <span class="font-jumperthin">{{ $cte(item.description) }}</span>
          </span>
        </div>
      </div>
    </template>

    <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
      <form class="register-form" @submit.prevent="handleSubmit(onSubmit)" novalidate>
        <fieldset v-if="currentStep === 1" class="register-step company-step">
          <validation-provider
              v-if="!step2ManualInput"
              mode="passive"
              :rules="{ required: true }"
              v-slot="validationContext"
          >
            <div class="auth-field">
              <label>Cégnév vagy adószám keresése</label>
              <DropDownSearch
                  v-model="companyData.name"
                  :options="selectOptions"
                  @select="selectCompanyName"
                  :required="true"
                  placeholder="Kezdj el gépelni..."
                  :class="{ 'is-invalid': validationContext.errors[0] }"
              />
            </div>
          </validation-provider>

          <validation-provider
              v-if="step2ManualInput"
              mode="lazy"
              :rules="{ required: true }"
              v-slot="validationContext"
          >
            <div class="auth-field">
              <label for="companyName">Cégnév</label>
              <input
                  v-model.trim="companyData.name"
                  id="companyName"
                  type="text"
                  :class="{ 'is-invalid': validationContext.errors[0] }"
              />
            </div>
          </validation-provider>

          <div v-if="step2ManualInput || selectedCompany" class="auth-field">
            <label for="company-seat">Székhely</label>
            <input
                v-model="companyData.address"
                id="company-seat"
                type="text"
                :readonly="isSelectedCompanyAddressReadonly"
                :disabled="!step2ManualInput && !selectedCompany"
            />
          </div>

          <div v-if="step2ManualInput || selectedCompany" class="auth-field">
            <label for="tax">Adószám</label>
            <masked-input
                v-model.trim="companyData.taxNumber"
                id="tax"
                mask="########-#-##"
                type="text"
                :readonly="isSelectedCompanyTaxNumberReadonly"
                :disabled="!step2ManualInput && !selectedCompany"
                :state="taxNumberInputState"
                placeholder="XXXXXXXX - X - XX"
            />
            <div v-if="taxNumberValid === true && maskedInputValid === false">
              <!--              <div class="feedback invalid-feedback d-block p-3">-->
              <!--                {{ $cte("validation.taxNumberLength") }}-->
              <!--              </div>-->
            </div>
            <div v-if="isTaxNumberRegistered === true" class="feedback invalid-feedback d-block">
              Ez az adószám már regisztrálva van.
            </div>
          </div>

          <label class="manual-toggle mb-5">
            <span class="switch-shell" :class="{ active: step2ManualInput }">
              <input type="checkbox" :checked="step2ManualInput" @change="onManualInputClick"/>
              <span></span>
            </span>
            <span class="font-jumperthin">Manuálisan adom meg</span>
          </label>
        </fieldset>

        <fieldset v-if="currentStep === 2" class="register-step">
          <b-form-row>
            <b-form-group class="col-12 col-md-6">
              <label for="last-name">Vezetéknév</label>
              <validation-provider mode="lazy" :rules="{ required: true }" v-slot="validationContext">
                <input
                    v-model.trim="personalData.lastName"
                    type="text"
                    id="last-name"
                    :class="{ 'is-invalid': validationContext.errors[0] }"
                />
              </validation-provider>
            </b-form-group>

            <b-form-group class="col-12 col-md-6">
              <label for="first-name">Keresztnév</label>
              <validation-provider mode="lazy" :rules="{ required: true }" v-slot="validationContext">
                <input
                    v-model.trim="personalData.firstName"
                    type="text"
                    id="first-name"
                    :class="{ 'is-invalid': validationContext.errors[0] }"
                />
              </validation-provider>
            </b-form-group>
          </b-form-row>

          <b-form-row>
            <b-form-group class="col-12 col-md-6">
              <label for="email">Email cím</label>
              <validation-provider
                  :rules="{ required: true, email: true, isMailUnique: true }"
                  v-slot="validationContext"
                  mode="lazy"
                  ref="email"
              >
                <input
                    v-model.trim="loginData.email"
                    autocomplete="email"
                    inputmode="email"
                    type="email"
                    id="email"
                    :class="{ 'is-invalid': validationContext.errors[0] }"
                    required
                />
              </validation-provider>
            </b-form-group>

            <b-form-group class="col-12 col-md-6">
              <label for="password">Jelszó</label>
              <div class="password-control">
                <div v-if="loginData.password && isPasswordFocused" class="password-strength-dropup">
                  <div
                      v-for="rule in passwordStrengthRules"
                      :key="rule.key"
                      class="password-strength-rule"
                      :class="{ fulfilled: rule.fulfilled }"
                  >
                    <span class="password-strength-icon" aria-hidden="true"></span>
                    <span>{{ rule.label }}</span>
                  </div>
                </div>
                <input
                    v-model="loginData.password"
                    name="password"
                    autocomplete="new-password"
                    :type="showPassword ? 'text' : 'password'"
                    id="password"
                    required
                    @focus="isPasswordFocused = true"
                    @blur="isPasswordFocused = false"
                />
                <button
                    type="button"
                    class="password-toggle"
                    aria-label="Jelszó láthatóság váltása"
                    @click="showPassword = !showPassword"
                >
                  <i v-if="showPassword" class="fal fa-eye-slash"></i>
                  <i v-else class="fal fa-eye"></i>
                </button>
              </div>
            </b-form-group>
          </b-form-row>

          <b-form-row>
            <b-form-group class="col-12">
              <label>Telefonszám</label>
              <div class="phone-control" @click.stop>
                <button
                    type="button"
                    class="phone-country-toggle"
                    :aria-label="selectedPhoneCountry.name"
                    @click="togglePhoneCountryDropdown"
                >
                  <span class="phone-flag" :class="selectedPhoneCountry.flagClass"></span>
                  <i class="fal fa-chevron-down fa-xs" aria-hidden="true"></i>
                </button>
                <masked-input
                    v-model="personalData.phone"
                    :mask="selectedPhoneCountry.mask"
                    inputmode="numeric"
                    @blur="onBlurPhone"
                    :placeholder="selectedPhoneCountry.placeholder"
                    :state="phoneValid"
                />
                <div v-if="phoneCountryDropdownOpen" class="phone-country-dropdown">
                  <input
                      v-model="phoneCountrySearch"
                      ref="phoneCountrySearch"
                      class="phone-country-search"
                      type="text"
                      placeholder="Ország keresése"
                  />
                  <div class="phone-country-list">
                    <button
                        v-for="country in filteredPhoneCountries"
                        :key="country.iso"
                        type="button"
                        class="phone-country-option"
                        @click="selectPhoneCountry(country)"
                    >
                      <span class="phone-country-flag phone-flag" :class="country.flagClass"></span>
                      <span class="phone-country-name">{{ country.name }}</span>
                      <span class="phone-country-dial">{{ country.dialCode }}</span>
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="validatePhone() === false" class="feedback invalid-feedback d-block">
                {{ $cte("validation.phone") }}
              </div>
            </b-form-group>
          </b-form-row>

          <label class="terms-row">
            <input v-model="termsAccepted" type="checkbox"/>
            <span class="terms-check" aria-hidden="true"></span>
            <span>Elfogadom az <a :href="termsLink" target="_blank">ÁSZF</a>-et és az <a :href="privacyLink"
                                                                                         target="_blank">adatvédelmi tájékoztatót</a>.</span>
          </label>
        </fieldset>

        <div
            class="register-actions"
            :class="{
            'register-actions-manual-off': currentStep === 1 && !step2ManualInput && !selectedCompany,
            'register-actions-manual-on': currentStep === 1 && (step2ManualInput || selectedCompany),
          }"
        >
          <button
              class="auth-submit"
              type="submit"
              ref="submit"
              @click="setFieldsDirty(['phone'])"
              :disabled="(currentStep === 1 && !isCompanyStepComplete()) || (currentStep === 2 && !termsAccepted)"
          >
            {{ currentStep === 1 ? 'Tovább' : 'Regisztrálok' }}
          </button>
        </div>
      </form>
    </ValidationObserver>
  </AuthLayout>
</template>

<script>
import Api from "api-client";
import DropDownSearch from "@/components/DropDownSearch";
import {mapActions} from "vuex";
import {ValidationProvider, ValidationObserver, extend} from "vee-validate";
import {required, email, confirmed} from "vee-validate/dist/rules";
import MaskedInput from "../../components/MaskedInput.vue";
import AuthLayout from "@/components/AuthLayout.vue";
import Util from "@/utils/Util";
import {
  armRegistrationCompletedMeasurement,
  disarmRegistrationCompletedMeasurement,
  measureOpenAiAdsOnce,
} from "@/utils/OpenAiAds";

extend("required", {
  ...required,
  message: "validation.required",
});

extend("email", {
  ...email,
  message: "validation.email",
});

extend("confirmed", {
  ...confirmed,
  message: "validation.emailSameAs",
});

extend("isMailUnique", {
  validate(value) {
    return Api.isEmailAvailablePublic(value)
        .then((res) => {
          return res.data;
        })
        .catch(() => {
          // even if the request is failed allow user to continue the registration
          return true;
        });
  },
  message: "validation.isMailUnique",
});

export default {
  components: {
    AuthLayout,
    DropDownSearch,
    ValidationProvider,
    ValidationObserver,
    MaskedInput,
  },
  watch: {
    taxNumber(val) {
      this.isTaxNumberRegistered = null;
      this.checkedTaxNumber = null;

      if (!val) {
        this.taxNumberValid = false;
        this.maskedInputValid = false;
        this.isTaxNumberAvailabilityChecking = false;
        this.taxNumberAvailabilityRequestId += 1;
        return;
      }

      if (val.length >= 1) {
        this.taxNumberValid = true;
      }
      if (this.isFullTaxNumber(val)) {
        this.maskedInputValid = true;
        this.checkTaxNumberAvailability(this.normalizeTaxNumber(val));
      } else {
        this.maskedInputValid = false;
        this.isTaxNumberAvailabilityChecking = false;
        this.taxNumberAvailabilityRequestId += 1;
      }
    },
    currentStep(val) {
      this.trackRegistrationStep(val);
    },
    "personalData.phone"(val) {
      this.phoneValid = this.isPhoneNumberValid(val);
    }
  },
  data() {
    return {
      step2ManualInput: false,
      maskedInputValid: false,
      taxNumberValid: false,
      isTaxNumberRegistered: null,
      isTaxNumberAvailabilityChecking: false,
      checkedTaxNumber: null,
      taxNumberAvailabilityRequestId: 0,
      validation: {
        phone: {
          dirty: false,
          valid: "",
        },
        birthDate: {
          dirty: false,
          valid: "",
        },
      },
      isStep2SubmitClicked: false,
      showPassword: false,
      isPasswordFocused: false,
      termsAccepted: false,
      selectedPhoneCountryIso: "HU",
      phoneCountryDropdownOpen: false,
      phoneCountrySearch: "",
      phoneCountries: [
        {
          iso: "HU",
          name: "Magyarország",
          flagClass: "flag-hu",
          dialCode: "+36",
          mask: "+36 ## ### ####",
          placeholder: "+36  30 123 4567",
          digitLength: 11
        },
        {
          iso: "AL",
          name: "Albánia",
          flagClass: "flag-al",
          dialCode: "+355",
          mask: "+355 ## ### ####",
          placeholder: "+355  68 123 4567",
          digitLength: 12
        },
        {
          iso: "AD",
          name: "Andorra",
          flagClass: "flag-ad",
          dialCode: "+376",
          mask: "+376 ### ###",
          placeholder: "+376  312 345",
          digitLength: 9
        },
        {
          iso: "AT",
          name: "Ausztria",
          flagClass: "flag-at",
          dialCode: "+43",
          mask: "+43 ### ### ####",
          placeholder: "+43  660 123 4567",
          digitLength: 12
        },
        {
          iso: "BE",
          name: "Belgium",
          flagClass: "flag-be",
          dialCode: "+32",
          mask: "+32 ### ## ## ##",
          placeholder: "+32  470 12 34 56",
          digitLength: 11
        },
        {
          iso: "BA",
          name: "Bosznia-Hercegovina",
          flagClass: "flag-ba",
          dialCode: "+387",
          mask: "+387 ## ### ###",
          placeholder: "+387  61 123 456",
          digitLength: 11
        },
        {
          iso: "BG",
          name: "Bulgária",
          flagClass: "flag-bg",
          dialCode: "+359",
          mask: "+359 ## ### ####",
          placeholder: "+359  88 123 4567",
          digitLength: 12
        },
        {
          iso: "CY",
          name: "Ciprus",
          flagClass: "flag-cy",
          dialCode: "+357",
          mask: "+357 ## ### ###",
          placeholder: "+357  96 123 456",
          digitLength: 11
        },
        {
          iso: "ME",
          name: "Montenegró",
          flagClass: "flag-me",
          dialCode: "+382",
          mask: "+382 ## ### ###",
          placeholder: "+382  67 123 456",
          digitLength: 11
        },
        {
          iso: "CZ",
          name: "Csehország",
          flagClass: "flag-cz",
          dialCode: "+420",
          mask: "+420 ### ### ###",
          placeholder: "+420  601 123 456",
          digitLength: 12
        },
        {
          iso: "DK",
          name: "Dánia",
          flagClass: "flag-dk",
          dialCode: "+45",
          mask: "+45 ## ## ## ##",
          placeholder: "+45  20 12 34 56",
          digitLength: 10
        },
        {
          iso: "GB",
          name: "Egyesült Királyság",
          flagClass: "flag-gb",
          dialCode: "+44",
          mask: "+44 #### ######",
          placeholder: "+44  7700 900123",
          digitLength: 12
        },
        {
          iso: "EE",
          name: "Észtország",
          flagClass: "flag-ee",
          dialCode: "+372",
          mask: "+372 #### ####",
          placeholder: "+372  5123 4567",
          digitLength: 11
        },
        {
          iso: "BY",
          name: "Fehéroroszország",
          flagClass: "flag-by",
          dialCode: "+375",
          mask: "+375 ## ### ## ##",
          placeholder: "+375  29 123 45 67",
          digitLength: 12
        },
        {
          iso: "FI",
          name: "Finnország",
          flagClass: "flag-fi",
          dialCode: "+358",
          mask: "+358 ## ### ####",
          placeholder: "+358  40 123 4567",
          digitLength: 12
        },
        {
          iso: "FR",
          name: "Franciaország",
          flagClass: "flag-fr",
          dialCode: "+33",
          mask: "+33 # ## ## ## ##",
          placeholder: "+33  6 12 34 56 78",
          digitLength: 11
        },
        {
          iso: "GR",
          name: "Görögország",
          flagClass: "flag-gr",
          dialCode: "+30",
          mask: "+30 ### ### ####",
          placeholder: "+30  691 123 4567",
          digitLength: 12
        },
        {
          iso: "NL",
          name: "Hollandia",
          flagClass: "flag-nl",
          dialCode: "+31",
          mask: "+31 # ########",
          placeholder: "+31  6 12345678",
          digitLength: 11
        },
        {
          iso: "HR",
          name: "Horvátország",
          flagClass: "flag-hr",
          dialCode: "+385",
          mask: "+385 ## ### ####",
          placeholder: "+385  91 123 4567",
          digitLength: 12
        },
        {
          iso: "IE",
          name: "Írország",
          flagClass: "flag-ie",
          dialCode: "+353",
          mask: "+353 ## ### ####",
          placeholder: "+353  87 123 4567",
          digitLength: 12
        },
        {
          iso: "IS",
          name: "Izland",
          flagClass: "flag-is",
          dialCode: "+354",
          mask: "+354 ### ####",
          placeholder: "+354  611 1234",
          digitLength: 10
        },
        {
          iso: "XK",
          name: "Koszovó",
          flagClass: "flag-xk",
          dialCode: "+383",
          mask: "+383 ## ### ###",
          placeholder: "+383  44 123 456",
          digitLength: 11
        },
        {
          iso: "PL",
          name: "Lengyelország",
          flagClass: "flag-pl",
          dialCode: "+48",
          mask: "+48 ### ### ###",
          placeholder: "+48  501 123 456",
          digitLength: 11
        },
        {
          iso: "LV",
          name: "Lettország",
          flagClass: "flag-lv",
          dialCode: "+371",
          mask: "+371 ## ### ###",
          placeholder: "+371  21 123 456",
          digitLength: 11
        },
        {
          iso: "LI",
          name: "Liechtenstein",
          flagClass: "flag-li",
          dialCode: "+423",
          mask: "+423 ### ## ##",
          placeholder: "+423  660 12 34",
          digitLength: 10
        },
        {
          iso: "LT",
          name: "Litvánia",
          flagClass: "flag-lt",
          dialCode: "+370",
          mask: "+370 ### #####",
          placeholder: "+370  612 34567",
          digitLength: 11
        },
        {
          iso: "LU",
          name: "Luxemburg",
          flagClass: "flag-lu",
          dialCode: "+352",
          mask: "+352 ### ### ###",
          placeholder: "+352  621 123 456",
          digitLength: 12
        },
        {
          iso: "MK",
          name: "Macedónia",
          flagClass: "flag-mk",
          dialCode: "+389",
          mask: "+389 ## ### ###",
          placeholder: "+389  70 123 456",
          digitLength: 11
        },
        {
          iso: "MT",
          name: "Málta",
          flagClass: "flag-mt",
          dialCode: "+356",
          mask: "+356 #### ####",
          placeholder: "+356  9912 3456",
          digitLength: 11
        },
        {
          iso: "MD",
          name: "Moldova",
          flagClass: "flag-md",
          dialCode: "+373",
          mask: "+373 ## ### ###",
          placeholder: "+373  69 123 456",
          digitLength: 11
        },
        {
          iso: "MC",
          name: "Monaco",
          flagClass: "flag-mc",
          dialCode: "+377",
          mask: "+377 ## ### ###",
          placeholder: "+377  63 123 456",
          digitLength: 11
        },
        {
          iso: "DE",
          name: "Németország",
          flagClass: "flag-de",
          dialCode: "+49",
          mask: "+49 ### ########",
          placeholder: "+49  151 12345678",
          digitLength: 13
        },
        {
          iso: "NO",
          name: "Norvégia",
          flagClass: "flag-no",
          dialCode: "+47",
          mask: "+47 ### ## ###",
          placeholder: "+47  412 34 567",
          digitLength: 10
        },
        {
          iso: "IT",
          name: "Olaszország",
          flagClass: "flag-it",
          dialCode: "+39",
          mask: "+39 ### ### ####",
          placeholder: "+39  320 123 4567",
          digitLength: 12
        },
        {
          iso: "RU",
          name: "Oroszország",
          flagClass: "flag-ru",
          dialCode: "+7",
          mask: "+7 ### ### ## ##",
          placeholder: "+7  912 345 67 89",
          digitLength: 11
        },
        {
          iso: "AM",
          name: "Örményország",
          flagClass: "flag-am",
          dialCode: "+374",
          mask: "+374 ## ### ###",
          placeholder: "+374  77 123 456",
          digitLength: 11
        },
        {
          iso: "PT",
          name: "Portugália",
          flagClass: "flag-pt",
          dialCode: "+351",
          mask: "+351 ### ### ###",
          placeholder: "+351  912 345 678",
          digitLength: 12
        },
        {
          iso: "RO",
          name: "Románia",
          flagClass: "flag-ro",
          dialCode: "+40",
          mask: "+40 ### ### ###",
          placeholder: "+40  712 123 456",
          digitLength: 11
        },
        {
          iso: "SM",
          name: "San Marino",
          flagClass: "flag-sm",
          dialCode: "+378",
          mask: "+378 #### ####",
          placeholder: "+378  1234 5678",
          digitLength: 11
        },
        {
          iso: "ES",
          name: "Spanyolország",
          flagClass: "flag-es",
          dialCode: "+34",
          mask: "+34 ### ### ###",
          placeholder: "+34  612 345 678",
          digitLength: 11
        },
        {
          iso: "CH",
          name: "Svájc",
          flagClass: "flag-ch",
          dialCode: "+41",
          mask: "+41 ## ### ## ##",
          placeholder: "+41  79 123 45 67",
          digitLength: 11
        },
        {
          iso: "SE",
          name: "Svédország",
          flagClass: "flag-se",
          dialCode: "+46",
          mask: "+46 ## ### ## ##",
          placeholder: "+46  70 123 45 67",
          digitLength: 11
        },
        {
          iso: "RS",
          name: "Szerbia",
          flagClass: "flag-rs",
          dialCode: "+381",
          mask: "+381 ## ### ####",
          placeholder: "+381  63 123 4567",
          digitLength: 12
        },
        {
          iso: "SK",
          name: "Szlovákia",
          flagClass: "flag-sk",
          dialCode: "+421",
          mask: "+421 ### ### ###",
          placeholder: "+421  905 123 456",
          digitLength: 12
        },
        {
          iso: "SI",
          name: "Szlovénia",
          flagClass: "flag-si",
          dialCode: "+386",
          mask: "+386 ## ### ###",
          placeholder: "+386  31 123 456",
          digitLength: 11
        },
        {
          iso: "TR",
          name: "Törökország",
          flagClass: "flag-tr",
          dialCode: "+90",
          mask: "+90 ### ### ## ##",
          placeholder: "+90  532 123 45 67",
          digitLength: 12
        },
        {
          iso: "UA",
          name: "Ukrajna",
          flagClass: "flag-ua",
          dialCode: "+380",
          mask: "+380 ## ### ## ##",
          placeholder: "+380  67 123 45 67",
          digitLength: 12
        },
        {
          iso: "VA",
          name: "Vatikán",
          flagClass: "flag-va",
          dialCode: "+379",
          mask: "+379 #### ####",
          placeholder: "+379  1234 5678",
          digitLength: 11
        },
      ],
      // LanogaSelect parameters
      selectOptions: {
        apiCallback: Api.getCompaniesAutoCompletePublic,
        formatText: function (val) {
          if (val && val.name) {
            return val.name;
          }
        },
        formatOption: function (val) {
          return val.name + " <br><small>" + val.fullTaxNumber + "</small><br><small>" + val.address + "</small>";
        },
        mapperCallback: function (data) {
          return Array.isArray(data) ? data : data.result;
        },
        minimumInputLength: 3,
        searchOnServer: true,
        locale: {
          searchPlaceholder: "select.search",
          noResults: "select.noResults",
        },
        idleHint: {
          threshold: 30,
          delay: 4000,
          title: "Nem találod a céged?",
          text: "Próbáld meg a <strong>teljes adószámot</strong><br>beírni a gyorsabb és pontosabb<br>szűrésért!",
        },
      },
      helpLink: process.env.VUE_APP_HELP_LINK,
      loginLink: '/login',
      privacyLink: 'https://acounto.com/adatkezelesi',
      termsLink: 'https://acounto.com/aszf',
      // Stepper state
      stepStates: [
        {
          id: 1,
          visible: true,
          disabled: false,
          validated: false,
          title: "regExt.companyStepTitle",
          description: "regExt.companyStepDesc",
        },
        {
          id: 2,
          visible: false,
          disabled: false,
          validated: false,
          title: "regExt.personalStepTitle",
          description: "regExt.personalStepDesc",
        },
      ],
      // Select options,
      countryOptions: [
        {
          value: "Magyarország",
          text: "Magyarország",
        },
      ],
      // Form model data
      loginData: {
        country: "",
        email: "",
        password: "",
        passwordAgain: "",
      },
      companyData: {
        name: "",
        shortName: "",
        address: "",
        hasAccountant: false,
        taxNumber: null,
        companyType: null,
        roleInTheCompany: null,
        employeeNumber: "",
        flatRateType: null,
        costRatio: "",
        taxFormStartDate: null,
      },
      personalData: {
        firstName: "",
        lastName: "",
        birthDate: "",
        phone: "",
      },
      selectedCompany: null,
      phoneValid: false,
      hidden: "",
    };
  },
  methods: {
    ...mapActions("app", ["login"]),

    pushDataLayerEvent(payload) {
      if (typeof window === "undefined") {
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    },

    trackRegistrationStep(step) {
      if (!step) {
        return;
      }

      this.pushDataLayerEvent({
        event: "registration_step_view",
        step: step,
        step_name: "step-" + step,
      });
    },

    trackCompleteRegistration() {
      this.pushDataLayerEvent({
        event: "complete_registration",
      });
    },

    init() {
      this.loginData.country = this.countryOptions[0].value;

      if (!this.personalData.phone) {
        this.personalData.phone = this.selectedPhoneCountry.dialCode + " ";
      }
    },

    setFieldsDirty(fieldsArray) {
      let self = this;
      if (self.currentStep === 2) {
        fieldsArray.forEach((field) => {
          self.validation[field].dirty = true;
        });
      }
    },

    validatePhone() {
      if (this.validation.phone.dirty) {
        if (this.personalData.phone) {
          const isValid = this.isPhoneNumberValid(this.personalData.phone);
          this.validation.phone.valid = isValid;
          this.phoneValid = isValid;
          return isValid;
        } else {
          this.validation.phone.valid = false;
          return false;
        }
      }
    },
    isPhoneNumberValid(value) {
      const digits = (value || "").replace(/\D/g, "");
      const dialDigits = this.selectedPhoneCountry.dialCode.replace(/\D/g, "");

      return digits.length === this.selectedPhoneCountry.digitLength && digits.startsWith(dialDigits);
    },
    normalizeTaxNumber(value) {
      const taxNumber = (value || "").trim();
      const digits = taxNumber.replace(/\D/g, "");

      if (/^\d{8}-\d-\d{2}$/.test(taxNumber)) {
        return taxNumber;
      }

      if (digits.length === 11) {
        return `${digits.slice(0, 8)}-${digits.slice(8, 9)}-${digits.slice(9, 11)}`;
      }

      return taxNumber;
    },
    isFullTaxNumber(value) {
      return /^\d{8}-\d-\d{2}$/.test(this.normalizeTaxNumber(value));
    },
    checkTaxNumberAvailability(taxNumber) {
      const requestId = this.taxNumberAvailabilityRequestId + 1;

      this.taxNumberAvailabilityRequestId = requestId;
      this.isTaxNumberAvailabilityChecking = true;

      Api.isTaxNumberRegisteredPublic(taxNumber)
          .then((res) => {
            if (requestId !== this.taxNumberAvailabilityRequestId || this.normalizeTaxNumber(this.taxNumber) !== taxNumber) {
              return;
            }

            this.isTaxNumberRegistered = res.data === true;
            this.checkedTaxNumber = taxNumber;
          })
          .catch(() => {
            if (requestId !== this.taxNumberAvailabilityRequestId || this.normalizeTaxNumber(this.taxNumber) !== taxNumber) {
              return;
            }

            this.isTaxNumberRegistered = null;
            this.checkedTaxNumber = null;
          })
          .finally(() => {
            if (requestId === this.taxNumberAvailabilityRequestId) {
              this.isTaxNumberAvailabilityChecking = false;
            }
          });
    },
    togglePhoneCountryDropdown() {
      this.phoneCountryDropdownOpen = !this.phoneCountryDropdownOpen;
      if (this.phoneCountryDropdownOpen) {
        this.$nextTick(() => {
          if (this.$refs.phoneCountrySearch) {
            this.$refs.phoneCountrySearch.focus();
          }
        });
      }
    },
    closePhoneCountryDropdown() {
      this.phoneCountryDropdownOpen = false;
    },
    selectPhoneCountry(country) {
      this.selectedPhoneCountryIso = country.iso;
      this.phoneCountryDropdownOpen = false;
      this.phoneCountrySearch = "";
      this.personalData.phone = country.dialCode + " ";
      this.phoneValid = false;
    },
    onManualInputClick() {
      this.step2ManualInput = !this.step2ManualInput;
      if (this.step2ManualInput) {
        this.selectedCompany = null;
      }
      this.companyData = {
        name: "",
        shortName: "",
        address: "",
        taxNumber: null,
        hasAccountant: false,
        companyType: null,
        roleInTheCompany: null,
        employeeNumber: "",
        flatRateType: null,
        costRatio: "",
        taxFormStartDate: null,
      };
      this.isTaxNumberRegistered = null;
      this.checkedTaxNumber = null;
      this.isTaxNumberAvailabilityChecking = false;
      this.taxNumberAvailabilityRequestId += 1;
      this.selectedCompany = null;
    },
    onBlurPhone: function () {
      this.validation.phone.dirty = true;
    },
    resetState() {
      Object.assign(this.$data, this.$options.data.call(this));
      this.$refs.observer.reset();
      this.init();
    },
    selectCompanyName(companySelectData) {
      this.selectedCompany = companySelectData;
      this.companyData.taxNumber = companySelectData.fullTaxNumber;
      this.companyData.address = companySelectData.address;
      this.companyData.companyType = companySelectData.collection;
      this.companyData.shortName = companySelectData.shortName;
    },
    onSubmit() {
      if (this.currentStep === 2) {
        this.validation.phone.dirty = true;
        const isPhoneValid = this.personalData.phone && this.phoneValid;
        this.validation.phone.valid = isPhoneValid;

        if (isPhoneValid && this.termsAccepted && this.isPasswordValid) {
          this.loginData.passwordAgain = this.loginData.password;
          this.saveUser();
        }
        return;
      }

      this.isStep2SubmitClicked = true;
      this.changeStep(this.currentStep + 1);
    },
    login(self, auth) {
      self.$store.dispatch("login/login", auth);
      self.$router.push("app/home");
    },
    saveUser() {
      let self = this;

      if (!this.step2ManualInput) {
        let nameFromApi = this.companyData.name;
        this.companyData.name = nameFromApi.name;

        if (!nameFromApi.shortName) {
          this.companyData.shortName = this.companyData.name;
        }
      }

      let model = {
        ...this.loginData,
        ...this.companyData,
        ...this.personalData,
      };

      if (!model.shortName || model.shortName !== "") {
        model.shortName = model.name;
      }

      model.screenWidth = window.screen.width;
      model.screenHeight = window.screen.height;
      model.meta = {
        ...(model.meta || {}),
        ...Util.getMarketingAttributionMetaTags(),
      };

      Api.signupExternal(model)
          .then(() => {
            self.trackCompleteRegistration();
            Util.storeLastSuccessfulLoginEmail(this.loginData.email);
            armRegistrationCompletedMeasurement();
            const navigation = self.$router.push({
              path: "/success-reg",
              query: {...self.$route.query},
            });

            navigation
                .then((failure) => {
                  if (failure) {
                    disarmRegistrationCompletedMeasurement();
                  }
                })
                .catch(() => {
                  disarmRegistrationCompletedMeasurement();
                });
          })
          .catch((error) => {
            const detailedMessage = error && error.response && error.response.data && error.response.data.detailedMessage;

            self.$toast.error({
              title: self.$cte("toast.register-external-failed"),
              message: detailedMessage || self.$cte("toast.register-external-failed"),
            });
            self.resetState();
          });

    },
    isCompanyStepComplete() {
      return !!(
          this.companyData.name &&
          this.companyData.address &&
          this.companyData.taxNumber &&
          this.maskedInputValid &&
          this.isTaxNumberAvailabilityChecked &&
          this.isTaxNumberRegistered === false
      );
    },
    onStepClick(number) {
      if (number === 2 && !this.isCompanyStepComplete()) {
        return;
      }

      this.changeStep(number);
    },
    changeStep(number) {
      if (number === 2 && !this.isCompanyStepComplete()) {
        return;
      }

      this.stepStates.forEach((elem) => {
        elem.visible = elem.id === number;
      });
      window.scrollTo(0, 0);
    },
  },
  computed: {
    currentStep() {
      let elem = this.stepStates.find((element) => element.visible);
      if (elem) {
        return elem.id;
      } else {
        return null;
      }
    },
    taxNumber: function () {
      return this.companyData.taxNumber;
    },
    normalizedTaxNumber() {
      return this.normalizeTaxNumber(this.companyData.taxNumber);
    },
    isSelectedCompanyAddressReadonly() {
      return !!(
          this.selectedCompany &&
          !this.step2ManualInput &&
          this.selectedCompany.address
      );
    },
    isSelectedCompanyTaxNumberReadonly() {
      return !!(
          this.selectedCompany &&
          !this.step2ManualInput &&
          this.isFullTaxNumber(this.selectedCompany.fullTaxNumber)
      );
    },
    isTaxNumberAvailabilityChecked() {
      return this.checkedTaxNumber === this.normalizedTaxNumber && this.isTaxNumberRegistered !== null;
    },
    taxNumberInputState() {
      if (this.taxNumberValid !== true) {
        return null;
      }

      if (!this.maskedInputValid) {
        return false;
      }

      if (this.isTaxNumberRegistered === true) {
        return false;
      }

      return this.isTaxNumberAvailabilityChecked && this.isTaxNumberRegistered === false ? true : null;
    },
    passwordStrengthRules() {
      const password = this.loginData.password || "";

      return [
        {
          key: "no-space",
          label: "Szóköz nem lehet",
          fulfilled: password.length > 0 && !/\s/.test(password),
        },
        {
          key: "lower-uppercase",
          label: "Kisbetű / nagybetű",
          fulfilled: /[a-záéíóöőúüű]/.test(password) && /[A-ZÁÉÍÓÖŐÚÜŰ]/.test(password),
        },
        {
          key: "number",
          label: "Szám",
          fulfilled: /\d/.test(password),
        },
        {
          key: "min-length",
          label: "Min. 8 karakter",
          fulfilled: password.length >= 8,
        },
      ];
    },
    isPasswordValid() {
      return this.passwordStrengthRules.every((rule) => rule.fulfilled);
    },
    selectedPhoneCountry() {
      return this.phoneCountries.find((country) => country.iso === this.selectedPhoneCountryIso) || this.phoneCountries[0];
    },
    filteredPhoneCountries() {
      const search = this.phoneCountrySearch.trim().toLowerCase();

      if (!search) {
        return this.phoneCountries;
      }

      return this.phoneCountries.filter((country) => {
        return country.name.toLowerCase().includes(search) || country.dialCode.includes(search);
      });
    },
  },
  created() {
    measureOpenAiAdsOnce("signup-start-page-viewed", "page_viewed", {
      type: "contents",
    });
    window.localStorage.clear();
    this.init();
    document.title = this.$cte("register.registerDocTitle");
    this.trackRegistrationStep(this.currentStep);
  },
  mounted() {
    document.addEventListener("click", this.closePhoneCountryDropdown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closePhoneCountryDropdown);
  },
};
</script>

<style scoped lang="scss">
.register-external-page::v-deep(.auth-page) {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
  overflow-x: hidden;
  box-sizing: border-box;
}

.register-external-page,
.register-external-page::v-deep(.auth-content),
.register-external-page::v-deep(.auth-card),
.register-form,
.register-step,
.form-row,
.form-group {
  max-width: 100%;
  min-width: 0;
}

.register-form .form-row {
  margin-left: 0;
  margin-right: 0;
}

.register-form > .register-step > .form-group {
  padding-left: 0;
  padding-right: 0;
}

.register-external-page::v-deep(.auth-card) {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.register-external-page::v-deep(.auth-title),
.register-external-page::v-deep(.auth-tabs) {
  flex: 0 0 auto;
}

.register-external-page::v-deep(.auth-title) {
  font-size: 26px;
}

@media (min-width: 577px) {
  .register-external-page::v-deep(.auth-title) {
    margin-top: 40px;
  }
}

.auth-step-list {
  display: flex;
  flex-direction: column;
  gap: clamp(24px, 4vh, 38px);
}

.auth-step-item {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: center;
  gap: 24px;
  color: rgba(255, 255, 255, 0.58);
  cursor: pointer;
}

.auth-step-counter {
  width: 76px;
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;
  border-radius: 50%;
  background: rgba(178, 236, 93, 0.65);
  color: rgba(0, 0, 0, 0.45);
  font-family: JumperLight, sans-serif;
  font-size: 30px;
}

.auth-step-item.active {
  color: #fff;
}

.auth-step-item.active .auth-step-counter {
  background: #b2ec5d;
  color: #000;
}

.auth-step-item.active .auth-step-counter {
  width: 92px;
  height: 92px;
  font-size: 36px;
}

.auth-step-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 20px;
  line-height: 1.05;
}

.auth-step-item.active .auth-step-copy {
  padding-left: 18px;
}

.auth-step-copy .font-jumperlight {
  font-family: JumperLight, sans-serif !important;
}

.auth-step-copy .font-jumperlight {
  font-family: JumperLight, sans-serif !important;
}

.auth-step-copy .font-jumperthin {
  font-family: JumperThin, sans-serif !important;
  font-size: 16px;
}

.register-form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.register-step {
  flex: 0 1 auto;
  border: 0;
  padding: 0;
  margin: 0;
}

.auth-field,
.form-group {
  margin-bottom: 18px;
}

.feedback.invalid-feedback {
  padding-left: 12px;
}

.auth-field label,
.form-group > legend,
.form-group > label {
  display: block;
  margin: 0 0 10px 24px;
  color: #000;
  font-family: JumperLight, sans-serif;
  font-size: 16px;
  line-height: 1;
  padding-left: 6px;
}

.form-group label {
  margin-left: 24px;
  padding-left: 6px;
}

.auth-field input,
.register-form input,
#app .register-form input,
#app .register-form .form-control {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  height: 40px !important;
  margin: 0 !important;
  padding: 0 20px !important;
  border: 1px solid #aebfc7 !important;
  border-radius: 999px !important;
  background: rgba(255, 255, 255, 0.65) !important;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
  color: #252734 !important;
  font-family: Poppins, sans-serif;
  font-size: 15px !important;
}

#app .register-form input:focus,
#app .register-form .form-control:focus {
  border-color: #b2ec5d !important;
  outline: none;
}

#app .register-form input.is-invalid,
#app .register-form .form-control.is-invalid {
  border-color: #f88379 !important;
}

#app .register-form input.is-invalid:focus,
#app .register-form .form-control.is-invalid:focus {
  border-color: #f88379 !important;
}

.register-form::v-deep(.dropdown-select.is-invalid .input-wrapper) {
  border-color: #f88379 !important;
}

.register-form::v-deep(.dropdown-select .input-wrapper),
.register-form::v-deep(.dropdown-select.is-open .input-wrapper),
.register-form::v-deep(input.select-search),
#app .register-form::v-deep(input.select-search) {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.password-control {
  position: relative;
}

.password-strength-dropup {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 10px);
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 24px;
  border: 1px solid #aebfc7;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.password-strength-rule {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #6e6e6e;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  line-height: 1.2;
}

.password-strength-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  border: 1px solid #aebfc7;
  border-radius: 50%;
  color: #b2ec5d;
  font-size: 11px;
}

.password-strength-icon::after {
  border-bottom: 2px solid #b2ec5d;
  border-left: 2px solid #b2ec5d;
  content: "";
  display: block;
  height: 7px;
  opacity: 0;
  transform: rotate(-45deg) translate(1px, -1px);
  width: 12px;
}

.password-strength-rule.fulfilled .password-strength-icon::after {
  opacity: 1;
}

.password-strength-rule.fulfilled .password-strength-icon {
  border-color: #b2ec5d;
  background: transparent;
}

.password-strength-rule.fulfilled {
  color: #252734;
}

.password-control input,
#app .password-control input {
  padding-right: 68px !important;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #252734;
  font-size: 18px;
  line-height: 1;
}

.phone-control {
  padding-top: 4px;
  position: relative;
  height: 40px;
  border: 1px solid #aebfc7;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.65);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.phone-control::v-deep(.form-control),
.phone-control::v-deep(.form-control.is-valid),
.phone-control::v-deep(.form-control.is-invalid),
#app .phone-control::v-deep(.form-control),
#app .phone-control::v-deep(.form-control.is-valid),
#app .phone-control::v-deep(.form-control.is-invalid) {
  width: calc(100% - 76px) !important;
  max-width: calc(100% - 76px) !important;
  height: 100% !important;
  margin-left: 76px !important;
  padding: 0 20px 0 0 !important;
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.phone-country-toggle {
  position: absolute;
  top: 50%;
  left: 5px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 54px;
  height: 30px;
  padding: 0;
  transform: translateY(-50%);
  border: 1px solid rgba(174, 191, 199, 0.75);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: #252734;
  font-size: 18px;
  line-height: 1;
}

.phone-country-toggle i {
  font-size: 6px;
  line-height: 1;
}

.phone-flag {
  display: inline-block;
  width: 21px;
  height: 21px;
  overflow: hidden;
  border-radius: 50%;
  background-size: cover;
  background-position: center;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.08);
}

.flag-hu {
  background: linear-gradient(to bottom, #ce2939 0 33.33%, #ffffff 33.33% 66.66%, #477050 66.66% 100%);
}

.flag-al {
  background: #e41e20;
}

.flag-ad {
  background: linear-gradient(to right, #10069f 0 33.33%, #fedd00 33.33% 66.66%, #d50032 66.66% 100%);
}

.flag-at {
  background: linear-gradient(to bottom, #ed2939 0 33.33%, #ffffff 33.33% 66.66%, #ed2939 66.66% 100%);
}

.flag-be {
  background: linear-gradient(to right, #000000 0 33.33%, #ffd90c 33.33% 66.66%, #ef3340 66.66% 100%);
}

.flag-ba {
  background: linear-gradient(135deg, #002395 0 65%, #fecb00 65% 100%);
}

.flag-bg {
  background: linear-gradient(to bottom, #ffffff 0 33.33%, #00966e 33.33% 66.66%, #d62612 66.66% 100%);
}

.flag-cy {
  background: #ffffff;
}

.flag-me {
  background: #c40308;
}

.flag-de {
  background: linear-gradient(to bottom, #000000 0 33.33%, #dd0000 33.33% 66.66%, #ffce00 66.66% 100%);
}

.flag-sk {
  background: linear-gradient(to bottom, #ffffff 0 33.33%, #0b4ea2 33.33% 66.66%, #ee1c25 66.66% 100%);
}

.flag-ro {
  background: linear-gradient(to right, #002b7f 0 33.33%, #fcd116 33.33% 66.66%, #ce1126 66.66% 100%);
}

.flag-hr {
  background: linear-gradient(to bottom, #ff0000 0 33.33%, #ffffff 33.33% 66.66%, #171796 66.66% 100%);
}

.flag-si {
  background: linear-gradient(to bottom, #ffffff 0 33.33%, #005da4 33.33% 66.66%, #ed1c24 66.66% 100%);
}

.flag-cz {
  background: linear-gradient(146deg, #11457e 0 38%, transparent 38%), linear-gradient(to bottom, #ffffff 0 50%, #d7141a 50% 100%);
}

.flag-dk {
  background: linear-gradient(to right, transparent 0 28%, #ffffff 28% 36%, transparent 36% 100%), linear-gradient(to bottom, #c8102e 0 42%, #ffffff 42% 56%, #c8102e 56% 100%);
}

.flag-ee {
  background: linear-gradient(to bottom, #4891d9 0 33.33%, #000000 33.33% 66.66%, #ffffff 66.66% 100%);
}

.flag-by {
  background: linear-gradient(to bottom, #d22730 0 70%, #00af66 70% 100%);
}

.flag-fi {
  background: linear-gradient(to right, transparent 0 32%, #002f6c 32% 46%, transparent 46% 100%), linear-gradient(to bottom, #ffffff 0 40%, #002f6c 40% 56%, #ffffff 56% 100%);
}

.flag-pl {
  background: linear-gradient(to bottom, #ffffff 0 50%, #dc143c 50% 100%);
}

.flag-it {
  background: linear-gradient(to right, #009246 0 33.33%, #ffffff 33.33% 66.66%, #ce2b37 66.66% 100%);
}

.flag-fr {
  background: linear-gradient(to right, #0055a4 0 33.33%, #ffffff 33.33% 66.66%, #ef4135 66.66% 100%);
}

.flag-gr {
  background: repeating-linear-gradient(to bottom, #0d5eaf 0 11.11%, #ffffff 11.11% 22.22%);
}

.flag-nl {
  background: linear-gradient(to bottom, #ae1c28 0 33.33%, #ffffff 33.33% 66.66%, #21468b 66.66% 100%);
}

.flag-ie {
  background: linear-gradient(to right, #169b62 0 33.33%, #ffffff 33.33% 66.66%, #ff883e 66.66% 100%);
}

.flag-is {
  background: linear-gradient(to right, transparent 0 30%, #ffffff 30% 44%, #d72828 44% 54%, #ffffff 54% 68%, transparent 68% 100%), linear-gradient(to bottom, #02529c 0 38%, #ffffff 38% 46%, #d72828 46% 56%, #ffffff 56% 64%, #02529c 64% 100%);
}

.flag-xk {
  background: #244aa5;
}

.flag-lv {
  background: linear-gradient(to bottom, #9e3039 0 40%, #ffffff 40% 60%, #9e3039 60% 100%);
}

.flag-li {
  background: linear-gradient(to bottom, #002b7f 0 50%, #ce1126 50% 100%);
}

.flag-lt {
  background: linear-gradient(to bottom, #fdb913 0 33.33%, #006a44 33.33% 66.66%, #c1272d 66.66% 100%);
}

.flag-lu {
  background: linear-gradient(to bottom, #ed2939 0 33.33%, #ffffff 33.33% 66.66%, #00a1de 66.66% 100%);
}

.flag-mk {
  background: #d20000;
}

.flag-mt {
  background: linear-gradient(to right, #ffffff 0 50%, #cf142b 50% 100%);
}

.flag-md {
  background: linear-gradient(to right, #0032a0 0 33.33%, #ffd100 33.33% 66.66%, #ce1126 66.66% 100%);
}

.flag-mc {
  background: linear-gradient(to bottom, #ce1126 0 50%, #ffffff 50% 100%);
}

.flag-no {
  background: linear-gradient(to right, transparent 0 28%, #ffffff 28% 42%, #00205b 42% 54%, #ffffff 54% 68%, transparent 68% 100%), linear-gradient(to bottom, #ba0c2f 0 36%, #ffffff 36% 46%, #00205b 46% 58%, #ffffff 58% 68%, #ba0c2f 68% 100%);
}

.flag-ru {
  background: linear-gradient(to bottom, #ffffff 0 33.33%, #0039a6 33.33% 66.66%, #d52b1e 66.66% 100%);
}

.flag-am {
  background: linear-gradient(to bottom, #d90012 0 33.33%, #0033a0 33.33% 66.66%, #f2a800 66.66% 100%);
}

.flag-pt {
  background: linear-gradient(to right, #006600 0 40%, #ff0000 40% 100%);
}

.flag-sm {
  background: linear-gradient(to bottom, #ffffff 0 50%, #5eb6e4 50% 100%);
}

.flag-es {
  background: linear-gradient(to bottom, #aa151b 0 25%, #f1bf00 25% 75%, #aa151b 75% 100%);
}

.flag-ch {
  background: #d52b1e;
}

.flag-se {
  background: linear-gradient(to right, transparent 0 30%, #fecc00 30% 42%, transparent 42% 100%), linear-gradient(to bottom, #006aa7 0 40%, #fecc00 40% 56%, #006aa7 56% 100%);
}

.flag-rs {
  background: linear-gradient(to bottom, #c6363c 0 33.33%, #0c4076 33.33% 66.66%, #ffffff 66.66% 100%);
}

.flag-tr {
  background: #e30a17;
}

.flag-ua {
  background: linear-gradient(to bottom, #0057b7 0 50%, #ffd700 50% 100%);
}

.flag-va {
  background: linear-gradient(to right, #ffe000 0 50%, #ffffff 50% 100%);
}

.flag-gb {
  background: linear-gradient(146deg, transparent 0 41%, #ffffff 41% 47%, #c8102e 47% 53%, #ffffff 53% 59%, transparent 59%), linear-gradient(34deg, transparent 0 41%, #ffffff 41% 47%, #c8102e 47% 53%, #ffffff 53% 59%, transparent 59%), linear-gradient(to bottom, transparent 0 35%, #ffffff 35% 43%, #c8102e 43% 57%, #ffffff 57% 65%, transparent 65%), linear-gradient(to right, #012169 0 35%, #ffffff 35% 43%, #c8102e 43% 57%, #ffffff 57% 65%, #012169 65% 100%);
}

.flag-us {
  background: repeating-linear-gradient(to bottom, #b22234 0 7.69%, #ffffff 7.69% 15.38%);
}

.phone-country-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  bottom: calc(100% + 10px);
  z-index: 10;
  padding: 14px;
  border: 1px solid #aebfc7;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.14);
}

.phone-country-search,
#app .phone-country-search {
  height: 38px !important;
  margin-bottom: 10px !important;
  box-shadow: none !important;
}

.phone-country-list {
  max-height: 230px;
  overflow-y: auto;
}

.phone-country-option {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 8px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  color: #252734;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  text-align: left;
}

.phone-country-option:hover {
  background: rgba(178, 236, 93, 0.25);
}

.phone-country-flag {
  width: 26px;
  height: 26px;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
}

.phone-country-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.phone-country-dial {
  color: #8a8a8a;
  font-size: 13px;
}

.manual-toggle,
.terms-row {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  margin: 10px 0 0 28px;
  font-size: 16px;
}

.manual-toggle .font-jumperthin {
  font-family: JumperThin, sans-serif !important;
}

.switch-shell {
  position: relative;
  width: 56px;
  height: 30px;
  display: inline-block;
  border-radius: 999px;
  background: #d7d7d7;
  transition: background 0.2s ease;
}

.switch-shell.active {
  background: #b2ec5d;
}

.switch-shell input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.switch-shell span {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;
}

.switch-shell.active span {
  transform: translateX(26px);
}

.terms-row {
  margin-top: 8px;
  margin-bottom: 30px;
  font-size: 13px;
  color: #6e6e6e;
}

.terms-row input,
#app .terms-row input {
  position: absolute;
  left: 0;
  width: 1px !important;
  min-width: 1px !important;
  max-width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  border: 0 !important;
  opacity: 0;
  pointer-events: none;
}

.terms-check {
  align-items: center;
  background: #ffffff;
  border: 1px solid #aebfc7;
  border-radius: 50%;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  color: #252734;
  display: inline-flex;
  flex: 0 0 auto;
  height: 26px;
  justify-content: center;
  transition: background 0.2s ease, border-color 0.2s ease;
  width: 26px;
}

.terms-check::after {
  border-bottom: 3px solid #252734;
  border-left: 3px solid #252734;
  content: "";
  display: block;
  height: 8px;
  opacity: 0;
  transform: rotate(-45deg) translate(1px, -1px);
  transition: opacity 0.2s ease;
  width: 14px;
}

.terms-row input:checked + .terms-check {
  background: #b2ec5d;
  border-color: #b2ec5d;
}

.terms-row input:checked + .terms-check::after {
  opacity: 1;
}

.terms-row a,
#app .terms-row a {
  color: #252734 !important;
  text-decoration: underline;
}

.register-actions {
  margin-top: auto;
  margin-bottom: clamp(22px, 2.5vw, 48px);
}

.register-actions-manual-off {
  margin-top: 163px;
}

.register-actions-manual-on {
  margin-top: -5px;
}

.auth-submit {
  width: 100%;
  min-height: 60px;
  border: 0;
  border-radius: 999px;
  background: #b2ec5d;
  box-shadow: 0 20px 30px rgba(0, 0, 0, 0.1);
  color: #252734;
  font-family: JumperLight, sans-serif;
  font-size: clamp(24px, 1.8vw, 32px);
  line-height: 1;
}

.auth-submit:disabled {
  opacity: 0.55;
}

.vue-tel-input {
  border: none;
  background: transparent;
}

@media (max-width: 576px) {
  .register-external-page::v-deep(.auth-page) {
    position: fixed;
    width: 100vw;
    max-width: 100vw;
    overflow: hidden;
  }

  .register-external-page::v-deep(.auth-content) {
    padding-left: 8px;
    padding-right: 8px;
  }

  .register-form {
    padding-left: 0;
    padding-right: 0;
  }

  .register-step > .form-row {
    display: block;
  }

  .auth-field label,
  .form-group > legend,
  .form-group > label {
    margin-left: 16px;
    font-size: 16px;
  }

  .form-group {
    width: 100%;
    padding-left: 0;
    padding-right: 0;
  }

  .terms-row {
    box-sizing: border-box;
    max-width: 100%;
    overflow: visible;
    padding-left: 8px;
  }

  .terms-row > span:last-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .auth-submit {
    min-height: 70px;
  }
}
</style>
